/**
 * Shared types + helpers for the Networking module. Pure — no React.
 * Professional + ConnectionStatus live in `src/types` as the app-wide
 * SSOT; re-exported here so intra-module imports stay short.
 */

export type { Professional, ConnectionStatus } from '../../types/professional';
import type { Professional, ConnectionStatus } from '../../types/professional';

export type SubView = 'connections' | 'requests' | 'discover';

/**
 * Accepts any DTO shape (snake_case from the BE, camelCase from tests /
 * older mocks) and normalises to the FE `Professional` type. Taking
 * `unknown` keeps the cast contained to this function and lets call
 * sites pass service DTOs (`id: number`, `connection_status`) without
 * the `as unknown as Record<string, unknown>` noise the reviewer flagged.
 */
export function mapProfessional(raw: unknown): Professional {
    const p = (raw ?? {}) as Record<string, unknown>;
    return {
        id: String(p.id ?? ''),
        name: String(p.name ?? ''),
        email: String(p.email ?? ''),
        role: String(p.role ?? ''),
        company: String(p.company ?? ''),
        location: String(p.location ?? ''),
        avatar: String(p.avatar ?? ''),
        online: Boolean(p.online),
        skills: Array.isArray(p.skills) ? p.skills.map(String) : [],
        connectionStatus: (p.connection_status ?? p.connectionStatus ?? 'none') as ConnectionStatus,
        connectionId: (p.connection_id ?? p.connectionId ?? null) as string | null,
        mutualCount: typeof p.mutual_count === 'number'
            ? (p.mutual_count as number)
            : typeof p.mutualCount === 'number'
                ? (p.mutualCount as number)
                : undefined,
    };
}

export function professionalMatches(p: Professional, query: string): boolean {
    if (!query) return true;
    const needle = query.toLowerCase();
    return (
        p.name.toLowerCase().includes(needle)
        || p.role.toLowerCase().includes(needle)
        || p.company.toLowerCase().includes(needle)
        || p.skills.some((s) => s.toLowerCase().includes(needle))
    );
}
